"use client";
import {
  BuyPageSearchParams,
  GpuInstanceTypeDropdown,
  useGPUInstanceType,
} from "@/components/buy-helpers";
import { EmptyState } from "@/components/empty-state";
import { MarketingText } from "@/components/marketing-text";
import { loadMotionFeatures } from "@/components/motion";
import { INTERNAL_LINKS } from "@/lib/helpers/urls";
import { AnimatePresence, LazyMotion, m } from "motion/react";
import AvgMinMaxChart, { type DataPoint } from "./AvgMinMaxChart";

export const PriceChartSection = ({
  pricesByHardwareType,
}: {
  pricesByHardwareType: Record<string, DataPoint[]>;
}) => {
  const [gpuType] = useGPUInstanceType();
  const { acc: historicalPrices } = (
    pricesByHardwareType[gpuType] ?? []
  ).reduce(
    ({ acc, prevLeftZero }, p) => {
      if (p?.avg > 0 || !prevLeftZero) acc.push(p);
      return {
        acc,
        prevLeftZero: prevLeftZero && p?.avg === 0,
      };
    },
    {
      acc: [] as DataPoint[],
      prevLeftZero: true,
    },
  );
  const floorPriceLabel = historicalPrices?.[0]?.bottom?.toFixed?.(2) ?? "-.--";
  return (
    <section>
      <MarketingText as="h2">
        <GpuInstanceTypeDropdown pricesByHardwareType={pricesByHardwareType} />{" "}
        from{" "}
        <LazyMotion features={loadMotionFeatures}>
          <AnimatePresence mode="wait">
            <m.span
              key={floorPriceLabel}
              className="font-medium"
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              ${floorPriceLabel}/gpu/hr
            </m.span>
          </AnimatePresence>
        </LazyMotion>
      </MarketingText>

      <div className="h-[320px] -mt-2">
        <AvgMinMaxChart
          data={historicalPrices}
          className="[&:not(:has(.pricing-empty-state))]:md:-ml-[50px] [&:not(:has(.pricing-empty-state))]:md:w-[calc(100%+50px)] [&:has(.pricing-empty-state)]:pt-4"
          emptyState={
            <EmptyState
              className="pricing-empty-state h-full"
              icon="Leaderboard"
              title={`No historical ${gpuType} pricing data yet`}
              description={"Get a custom quote while we collect pricing data."}
              primaryAction={{
                label: `Get a ${gpuType} quote`,
                type: "link",
                href: `${INTERNAL_LINKS.BUY}?${BuyPageSearchParams.GPU_TYPE}=${gpuType}`,
              }}
            />
          }
        />
      </div>
    </section>
  );
};
